import { ReplaySubject } from "rxjs";
import { IOfficeHost } from "@core/shared/office-hosts/office-host.types";
import { logger } from "@services/logger/logger.service";

const AUTH_MESSAGE_TYPE = 'r2-copilot-login'

interface IAuthModalParams
{
    message: string;
    origin: string | undefined;
    error?: number;
}

interface IAuthModalMessage
{
    type: typeof AUTH_MESSAGE_TYPE;
    isLoggedIn: boolean;
}

export abstract class OfficeBase
{
    public initState$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    protected hostType!: Office.HostType;
    protected platformType!: Office.PlatformType;
    protected userEmail: string = '';
    protected currentHost!: IOfficeHost;

    constructor()
    {
        this.init();
    }

    public get userHint(): string
    {
        return this.userEmail;
    }

    private async init(): Promise<void>
    {
        await Office.onReady(async (info: { host: Office.HostType, platform: Office.PlatformType; }) =>
        {
            try
            {
                this.platformType = info.platform;

                if (info.platform && info.host)
                {
                    logger.log(this.constructor.name, 'init', `Office.js is ready. Platform: ${info.platform}, Host: ${info.host}`, 'log');

                    this.hostType = info.host;
                    this.platformType = info.platform;

                    await this.updateUserProfile();

                    const isCreated = Boolean(this.currentHost);
                    const host = this.getHost();
                    await host.waitReadyAsync();

                    if (!isCreated)
                    {
                        host.attachHandler(Office.EventType.ItemChanged, this.officeItemChanged.bind(this));
                    }

                    this.initState$.next(true);
                    return;
                }

                this.initState$.next(false);
            }
            catch (e)
            {
                logger.error(this.constructor.name, 'init', 'Error during Office.onReady callback', 'log', e);
                this.initState$.next(false);
            }
        });
    }

    public async showDialog(
        startAddress: string,
        options?: Office.DialogOptions,
    ): Promise<void>
    {
        return new Promise((resolve, reject) =>
        {
            logger.log(startAddress);
            const windowOptions = {
                width: 50,                  // percentage of current display
                height: 50,                 // percentage of current display
                // promptBeforeOpen: false, // not work by default for Firefox, Safari, IE. Reconfig default settings required.
                // displayInIframe: true,   // not work: Refused to frame 'https://login-stage.hypersecureid.com/' because an ancestor violates the following Content Security Policy directive: "frame-ancestors 'self'".
                ...options,
            };

            Office.context.ui.displayDialogAsync(startAddress, windowOptions, (res) =>
            {
                logger.log('ui.displayDialogAsync', res)

                if (res.status === Office.AsyncResultStatus.Succeeded)
                {
                    const dialog = res.value;

                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    dialog.addEventHandler(Office.EventType.DialogMessageReceived, onMessageReceived.bind(null, dialog));
                } else
                {
                    logger.error('Error displaying dialog:', res.error);
                    reject(res.error)
                }

                async function onMessageReceived(dialog: Office.Dialog, data: IAuthModalParams)
                {
                    if (data?.error)
                    {
                        reject(`Message from auth modal error: ${data.error}`)
                    }

                    const message = JSON.parse(data.message) as unknown as IAuthModalMessage;
                    if (message.type === AUTH_MESSAGE_TYPE && message.isLoggedIn)
                    {
                        try
                        {
                            dialog.close()
                        } catch (e)
                        {
                            logger.error(e)
                        }

                        resolve();
                    }
                }
            });
        })
    }

    public async setSelectedTextAsync(plainText: string): Promise<void>
    {
        const host = this.getHost();
        if (host?.checkEditable() === true)
        {
            await host.setSelectedTextAsync(plainText);
        }
    }

    public async setSelectedImageAsync(filename: string, b64Text: string): Promise<void>
    {
        const host = this.getHost();
        if (host?.checkEditable() === true)
        {
            await host.setSelectedImageAsync(filename, b64Text);
        }
    }

    abstract getHost(): IOfficeHost

    private async updateUserProfile(): Promise<void>
    {
        try
        {
            if (this.hostType == Office.HostType.Outlook)
            {
                const userProfile = Office.context.mailbox?.userProfile;
                this.userEmail = userProfile.emailAddress;

                return;
            }

            // const options: Office.AuthOptions = {
            //     allowSignInPrompt: false,
            //     allowConsentPrompt: false,
            //     forMSGraphAccess: false
            // };

            // console.log('Requesting access token with options: ', options);
            // // eslint-disable-next-line prefer-const
            // const timeout = new Promise<never>((_, reject) =>
            //     setTimeout(() => reject(new Error('getAccessToken timeout')), 3000)
            // );
            // const tokenData = await Promise.race([Office.auth.getAccessToken(options), timeout]);
            // console.log('Access token data received: ', tokenData);
            // const parts = tokenData.split(".");
            // const token = JSON.parse(atob(parts[1]));
            // token.
            return
        } catch (e)
        {
            logger.error(e);
        }
    }

    private officeItemChanged(event: Event)
    {
        logger.log(`officeItemChanged(${JSON.stringify(event.type)})`)

        if (this.onItemChanged)
        {
            this.onItemChanged(event);
        }
    }

    private onItemChanged(event: Event): void
    {
        logger.log(event)
        logger.log('CALL ON ITEM CHANGE FN');
    }
}
